import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Download, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { Overline, Ornament } from "@/components/Brand";
import { PlacementCard } from "@/components/PlacementCard";
import EmailRoadmap from "@/components/EmailRoadmap";
import { MoonGlyph } from "@/components/PersonalInfo";
import { RETAKE_TEXT } from "@/data/interpretations";
import { generateRoadmapPdf, cosmicRows } from "@/lib/pdf";

export default function Results({ profile, meta, roadmap, onRetake }) {
  const [busy, setBusy] = useState(false);
  const name = profile.firstName.trim();
  const P = roadmap.placements;

  const download = async () => {
    setBusy(true);
    try {
      await generateRoadmapPdf({ profile: { ...profile, firstName: name }, meta, roadmap });
      toast.success("Your Goddess Journey Roadmap has been downloaded.");
    } catch {
      toast.error("Something interrupted the download. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="results-roadmap-container" className="px-5 sm:px-10 pb-24">
      <section className="max-w-6xl mx-auto pt-4 sm:pt-10 grid lg:grid-cols-[1fr_340px] gap-10 items-end">
        <div>
          <Overline>Your Personalized Results</Overline>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#4A2A20] mt-4 leading-[1.05]">
            Your Goddess Journey <span className="italic gold-text">Roadmap</span>
          </h1>
          <p className="font-serif-display italic text-xl sm:text-2xl text-[#9A5547] mt-4" data-testid="results-greeting">Beloved {name}, here is where your path begins.</p>
          <p className="text-[#7D5A4B] mt-4 max-w-2xl">This roadmap reflects where you are right now. It is a loving snapshot, not a fixed label, and every journey remains open to you.</p>
          <button data-testid="download-pdf-btn" onClick={download} disabled={busy} className="btn-rose rounded-full px-8 sm:px-10 py-5 text-base sm:text-lg font-medium inline-flex items-center gap-3 mt-8">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
            Download My Goddess Journey Roadmap
          </button>
        </div>
        <MetaPanel meta={meta} />
      </section>

      <section className="max-w-6xl mx-auto mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="journey-summary">
        {["primary", "secondary", "interwoven", "later"].map((k, i) => (
          <motion.div key={k} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * i }} className="rounded-2xl p-4 border border-[#C9A24A]/25" style={{ background: P[k].journey.tint }}>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#B8892F]">{k === "primary" ? "Begin Here" : k === "secondary" ? "Develop Next" : k === "interwoven" ? "Explore Alongside" : "Revisit Later"}</div>
            <div className="font-serif-display text-lg text-[#4A2A20] mt-1 leading-tight" data-testid={`summary-${k}`}>{P[k].journey.name}</div>
          </motion.div>
        ))}
      </section>

      <div className="max-w-6xl mx-auto mt-14 space-y-10">
        {["primary", "secondary", "interwoven", "later"].map((k, i) => <PlacementCard key={k} pl={P[k]} index={i} />)}
      </div>

      <FirstSteps steps={roadmap.steps} />
      <CosmicSnapshot profile={profile} />

      <section className="max-w-6xl mx-auto mt-16 grid lg:grid-cols-2 gap-6">
        <EmailRoadmap profile={profile} meta={meta} roadmap={roadmap} />
        <div data-testid="retake-section" className="rounded-3xl p-6 sm:p-8 bg-gradient-to-br from-[#FBEFEA] to-[#FAF3E8] border border-[#C9A24A]/30">
          <Overline>Evolve & Return</Overline>
          <h3 className="font-serif-display text-2xl sm:text-3xl text-[#4A2A20] mt-2">Return to Your Goddess Journey</h3>
          <p className="text-sm text-[#7D5A4B] mt-3 leading-relaxed italic">{RETAKE_TEXT}</p>
          <p className="text-xs text-[#9A5547] mt-3">Starting over clears every answer from this page. Please download your roadmap first if you'd like to keep it.</p>
          <button data-testid="retake-assessment-btn" onClick={onRetake} className="btn-ghost-gold rounded-full px-7 py-3 text-sm mt-6 inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Begin a New Assessment
          </button>
        </div>
      </section>
    </main>
  );
}

const MetaPanel = ({ meta }) => (
  <div className="glass-card rounded-3xl p-6" data-testid="assessment-meta">
    <div className="flex items-center gap-4">
      <MoonGlyph illumination={meta.lunar.illumination} waxing={meta.lunar.age < 14.77} />
      <div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#B8892F]">Lunar phase</div>
        <div className="font-serif-display text-2xl text-[#4A2A20]" data-testid="results-lunar-phase">{meta.lunar.name}</div>
      </div>
    </div>
    <p className="font-serif-display italic text-[17px] leading-snug text-[#9A5547] mt-4" data-testid="lunar-message">{meta.lunar.message}</p>
    <dl className="mt-4 text-sm space-y-1.5">
      {[["Date", meta.date], ["Time", meta.time], ["Time zone", meta.timeZone]].map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4"><dt className="text-[#7D5A4B]">{k}</dt><dd className="text-[#4A2A20] text-right" data-testid={`meta-${k.toLowerCase().replace(" ", "-")}`}>{v}</dd></div>
      ))}
    </dl>
  </div>
);

const FirstSteps = ({ steps }) => (
  <section className="max-w-6xl mx-auto mt-20" data-testid="first-steps-section">
    <Overline>Begin Gently</Overline>
    <h2 className="font-serif-display text-3xl sm:text-4xl text-[#4A2A20] mt-3">Your First Three Goddess Steps</h2>
    <Ornament className="mt-4" />
    <div className="grid md:grid-cols-3 gap-5 mt-8">
      {steps.map((s, i) => (
        <div key={i} data-testid={`first-step-${i + 1}`} className={`rounded-3xl p-6 border border-[#C9A24A]/30 bg-white/80 ${i === 1 ? "md:mt-8" : ""}`}>
          <div className="flex items-center gap-3">
            <span className="font-serif-display text-4xl gold-text">{i + 1}</span>
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#B8892F]">{s.label}</span>
          </div>
          <p className="text-[15px] text-[#4A2A20] mt-4 leading-relaxed">{s.action}</p>
          <p className="text-xs text-[#7D5A4B] mt-4 flex gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#C9A24A] shrink-0" />{s.pathway}</p>
        </div>
      ))}
    </div>
  </section>
);

const CosmicSnapshot = ({ profile }) => {
  const rows = cosmicRows(profile);
  if (!rows.length) return null;
  return (
    <section className="max-w-6xl mx-auto mt-16 rounded-3xl p-6 sm:p-8 glass-card" data-testid="cosmic-snapshot">
      <Overline>Optional</Overline>
      <h3 className="font-serif-display text-2xl sm:text-3xl text-[#4A2A20] mt-2">Your Personal Cosmic Snapshot</h3>
      <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {rows.map(([k, v]) => (
          <div key={k} className="rounded-2xl p-4 bg-[#FAF3E8]/80 border border-[#C9A24A]/20">
            <dt className="text-[10px] tracking-[0.2em] uppercase text-[#B8892F]">{k}</dt>
            <dd className="font-serif-display text-xl text-[#4A2A20] mt-1">{v}</dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-[#7D5A4B] mt-5">These details are recorded for context and possible future use in a Personal Goddess Goldprint. They did not influence your journey results.</p>
    </section>
  );
};
